import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, CheckCircle, AlertCircle, Loader2, 
  ExternalLink, RefreshCw, DollarSign, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { stripeConnectApi, earningsApi } from '@/api';
import { toast } from 'sonner';

const StripeConnectCard = ({ user }) => {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchStatus();
    
    // Check for Stripe return
    if (searchParams.get('stripe_return') === 'true') {
      toast.success(i18n.language === 'fr' 
        ? 'Retour de Stripe - Vérification du statut...' 
        : 'Returned from Stripe - Checking status...');
      // Clean URL
      searchParams.delete('stripe_return');
      setSearchParams(searchParams);
      // Refresh status after a moment
      setTimeout(fetchStatus, 1000);
    }
    
    if (searchParams.get('stripe_refresh') === 'true') {
      toast.info(i18n.language === 'fr' 
        ? 'Lien expiré - Veuillez réessayer' 
        : 'Link expired - Please try again');
      searchParams.delete('stripe_refresh');
      setSearchParams(searchParams);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const [statusData, earningsData] = await Promise.all([
        stripeConnectApi.getStatus(),
        earningsApi.getExplorerEarnings().catch(() => null)
      ]);
      setStatus(statusData);
      setEarnings(earningsData);
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setActionLoading(true);
    try {
      const { url } = await stripeConnectApi.createAccountLink();
      window.location.href = url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du lien');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl border p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const isConnected = status?.charges_enabled && status?.payouts_enabled;
  const isPending = status?.has_account && !isConnected;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl border overflow-hidden"
      data-testid="stripe-connect-card"
    >
      {/* Header */}
      <div className={`p-4 ${isConnected ? 'bg-green-50' : isPending ? 'bg-yellow-50' : 'bg-orange-50'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isConnected ? 'bg-green-100' : isPending ? 'bg-yellow-100' : 'bg-orange-100'
          }`}>
            {isConnected ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : isPending ? (
              <Clock className="w-5 h-5 text-yellow-600" />
            ) : (
              <CreditCard className="w-5 h-5 text-orange-600" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {i18n.language === 'fr' ? 'Paiements Stripe' : 'Stripe Payments'}
            </h3>
            <p className="text-sm text-gray-600">
              {isConnected 
                ? (i18n.language === 'fr' ? 'Compte connecté' : 'Account connected')
                : isPending
                  ? (i18n.language === 'fr' ? 'Configuration en cours' : 'Setup in progress')
                  : (i18n.language === 'fr' ? 'Non configuré' : 'Not configured')
              }
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {isConnected ? (
          <>
            {/* Earnings Summary */}
            {earnings && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <DollarSign className="w-6 h-6 mx-auto text-green-600 mb-1" />
                  <div className="text-2xl font-bold text-green-700">{earnings.released_earnings.toFixed(2)}€</div>
                  <div className="text-xs text-green-600">
                    {i18n.language === 'fr' ? 'Gains reçus' : 'Received'}
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 text-center">
                  <Clock className="w-6 h-6 mx-auto text-yellow-600 mb-1" />
                  <div className="text-2xl font-bold text-yellow-700">{earnings.pending_earnings.toFixed(2)}€</div>
                  <div className="text-xs text-yellow-600">
                    {i18n.language === 'fr' ? 'En attente' : 'Pending'}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 rounded-lg p-3">
              <CheckCircle className="w-4 h-4" />
              <span>
                {i18n.language === 'fr' 
                  ? 'Vous pouvez recevoir des paiements des voyageurs'
                  : 'You can receive payments from travelers'}
              </span>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleStartOnboarding}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4 mr-2" />
              )}
              {i18n.language === 'fr' ? 'Gérer mon compte Stripe' : 'Manage Stripe account'}
            </Button>
          </>
        ) : isPending ? (
          <>
            <div className="flex items-start gap-2 text-sm text-yellow-700 bg-yellow-50 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">
                  {i18n.language === 'fr' 
                    ? 'Configuration incomplète'
                    : 'Setup incomplete'}
                </p>
                <p className="text-yellow-600 mt-1">
                  {i18n.language === 'fr' 
                    ? 'Complétez votre profil Stripe pour recevoir des paiements.'
                    : 'Complete your Stripe profile to receive payments.'}
                </p>
              </div>
            </div>
            
            {status.requirements?.currently_due?.length > 0 && (
              <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                <span className="font-medium">{i18n.language === 'fr' ? 'Documents requis:' : 'Required:'}</span>
                <ul className="mt-1 list-disc list-inside">
                  {status.requirements.currently_due.slice(0, 3).map((req, i) => (
                    <li key={i}>{req}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button 
                className="flex-1"
                onClick={handleStartOnboarding}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                {i18n.language === 'fr' ? 'Continuer la configuration' : 'Continue setup'}
              </Button>
              <Button 
                variant="outline"
                onClick={fetchStatus}
                disabled={actionLoading}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              {i18n.language === 'fr' 
                ? 'Connectez votre compte bancaire via Stripe pour recevoir les paiements de vos expériences.'
                : 'Connect your bank account via Stripe to receive payments from your experiences.'}
            </p>
            
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {i18n.language === 'fr' ? 'Paiements sécurisés' : 'Secure payments'}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {i18n.language === 'fr' ? 'Virements automatiques' : 'Automatic transfers'}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {i18n.language === 'fr' ? 'Suivi des revenus' : 'Revenue tracking'}
              </li>
            </ul>
            
            <Button 
              className="w-full"
              onClick={handleStartOnboarding}
              disabled={actionLoading}
              data-testid="stripe-connect-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              {i18n.language === 'fr' ? 'Configurer Stripe' : 'Set up Stripe'}
            </Button>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default StripeConnectCard;
